import type { TextInputFieldProps } from '@monthio/react-components/dist/Form/TextInputField'
import type {
	AutocompletedVehicle,
	LookupAddressQuery,
	LookupAddressResult,
} from '#modules/AssetOverview/Tinglysning.types'

import { InputAdornment, makeStyles } from '@material-ui/core'
import AutoComplete from '@material-ui/lab/Autocomplete'
import {
	COLORS,
	MaterialIcons,
	TextInputField,
	useDebounce,
} from '@monthio/react-components'
import { tinglysningApiClient } from '#api/tinglysningApiClient'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import type { AutocompletedAddress } from '../../../../api/addressApi'

import { addressSearch } from '../../../../api/addressApi'

interface Option {
	key: string
	defaultDisplayValue: string
}

const defaultRenderer = (o: Option) => o.defaultDisplayValue

const OptionContainer = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	padding: 4px 8px;
`

const useAutoCompleteStyles = makeStyles({
	'@global': {
		'.MuiAutocomplete-listbox': {
			background: COLORS.GREY_SCALE.HOVER_GREY,
			display: 'flex',
			gap: '2px',
			padding: 0,
			flexDirection: 'column',
			maxHeight: 'unset',
		},
		'.MuiAutocomplete-option': {
			padding: 0,
		},

		'.MuiAutocomplete-option:has(>div[data-autocomplete])': {
			background: COLORS.GREY_SCALE.WHITE,
		},
		'.MuiAutocomplete-option[data-focus="true"]:has(>div[data-autocomplete])': {
			background: COLORS.GREY_SCALE.HOVER_GREY,
		},
	},
})

function identity<T>(t: T): T {
	return t
}

export function AutoCompleteGeneric<T extends Option>({
	value,
	onChange,
	search,
	renderOption,
	renderValue,
	placeholder,
}: {
	value?: T | null
	onChange: (t?: T | null) => void
	search: (term?: string) => Promise<T[] | null>
	renderOption?: (o: T) => JSX.Element
	renderValue?: (o: T) => string
	placeholder?: string
}): JSX.Element {
	const [options, setOptions] = useState<T[]>([])
	const currentTerm = useRef<string>()
	const debouncedSearch = useDebounce(search, 300)

	const onInputChange = useCallback(
		async (_, term) => {
			currentTerm.current = term
			if (term) {
				const data = await debouncedSearch(term)
				if (data) setOptions(data)
			} else {
				setOptions([])
			}
		},
		[debouncedSearch],
	)

	useAutoCompleteStyles()

	return (
		<AutoComplete
			value={value}
			freeSolo
			// if we do not clear autocomplete, it leaves styles for button base, which breaks other selects in development
			clearOnBlur
			blurOnSelect
			clearOnEscape
			forcePopupIcon={false}
			options={options}
			onChange={(_, value) => {
				value &&
					options.some((x) => x.key === (value as T).key) &&
					onChange(value as T | null)
			}}
			getOptionLabel={renderValue || defaultRenderer}
			onInputChange={onInputChange}
			filterOptions={identity}
			renderOption={(o) => (
				<OptionContainer data-autocomplete>
					{(renderOption || renderValue || defaultRenderer)(o)}
				</OptionContainer>
			)}
			renderInput={({
				...params
			}: JSX.IntrinsicAttributes & TextInputFieldProps) => (
				<TextInputField
					{...params}
					placeholder={placeholder}
					variant="outlined"
					InputProps={{
						...params.InputProps,
						endAdornment: params.inputProps?.value ? (
							params.InputProps?.endAdornment
						) : (
							<InputAdornment position="end">
								<MaterialIcons.Search style={{ translate: 32 }} />
							</InputAdornment>
						),
					}}
				/>
			)}
			noOptionsText={
				(currentTerm.current?.length || 0) < 3 ? 'Type to search' : 'No matches'
			}
		/>
	)
}

const Row = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: baseline;
`

const Bold = styled.div`
	font-weight: 700;
`

const Description = styled(Row)`
	font-size: 0.8rem;
`

export function toCased(s?: string): string {
	if (!s) return ''
	const [first, ...rest] = s
	return `${first!.toUpperCase()}${rest.join('').toLowerCase()}`
}

const u = ['uoplyst', 'ukendt', '-']

function isNonEmpty(s: string): boolean {
	return !!s && !u.includes(s.toLowerCase())
}

function joinNonEmpty(parts: string[], joiner: string): string {
	return parts
		.filter((p) => isNonEmpty(p))
		.map((x) => toCased(x))
		.join(joiner)
}

function getDescriptions(vehicle: AutocompletedVehicle): string[] {
	const parts = [
		[vehicle.modelYear?.toString(), vehicle.brand, vehicle.model],
		[vehicle.variant],
	]
	return parts.map((x) => joinNonEmpty(x, ' ')).filter((x) => isNonEmpty(x))
}

export const AutoCompleteVehicle: React.FC<{
	onChange: (vehicle: AutocompletedVehicle) => void
	children?: never
	registrationNumber?: string
}> = ({ onChange, registrationNumber }) => {
	const [value, setValue] = useState<(Option & AutocompletedVehicle) | null>(
		registrationNumber
			? ({
					registration: registrationNumber,
				} as Option & AutocompletedVehicle)
			: null,
	)
	const onSearch = useSearch(vehicleSearch)
	return (
		<AutoCompleteGeneric
			value={value}
			onChange={(x) => {
				setValue(null)
				onChange(x as AutocompletedVehicle)
			}}
			placeholder="DN69597"
			search={onSearch}
			renderOption={(o) => (
				<>
					<Row>
						<Bold>{o.registration}</Bold>
						<Description>{toCased(o.kind)}</Description>
					</Row>

					{getDescriptions(o).map((d, i) => (
						<Description key={i}>{d}</Description>
					))}
				</>
			)}
		/>
	)
}

async function autoCompleteAddress(
	signal: AbortSignal,
	addressTerm?: string,
): Promise<(Option & AutocompletedAddress)[]> {
	const addresses = await addressSearch(signal, addressTerm)
	return addresses.map(({ id, betegnelse, ...address }) => ({
		key: id,
		defaultDisplayValue: betegnelse,
		betegnelse,
		id,
		...address,
	}))
}

const buildAddressQuery = (
	address: AutocompletedAddress,
): LookupAddressQuery => ({
	street: address.adresseringsvejnavn,
	streetNumber: address.husnr,
	postalCode: parseInt(address.postnr),
	floor: address.etage,
	door: address.dør,
})

export const AutoCompleteAddress: React.FC<{
	onChange: (address: LookupAddressResult) => void
	children?: never
}> = ({ onChange }) => {
	const [value, setValue] = useState<(Option & AutocompletedAddress) | null>(
		null,
	)
	const onSearch = useSearch(autoCompleteAddress)

	const onAddressChange = useCallback(
		async (address: AutocompletedAddress) => {
			const addressQuery = buildAddressQuery(address)
			const tinglysningAddress = await tinglysningApiClient
				.lookupAddress(addressQuery)
				.catch((error) => {
					console.error(error)
					return {
						bfeNumber: undefined,
						address: address.betegnelse,
						realEstateValue: 0,
					}
				})

			onChange(tinglysningAddress)
		},
		[onChange],
	)

	return (
		<AutoCompleteGeneric
			value={value}
			onChange={(address) => {
				setValue(null)
				onAddressChange(address as AutocompletedAddress)
			}}
			placeholder="Bøgevej 20, 6800 Varde"
			search={onSearch}
			renderOption={(o) => (
				<>
					<Bold>{`${o.vejnavn} ${[
						o.husnr,
						[o.etage, o.dør].filter((x) => !!x).join(' '),
					]
						.filter((x) => !!x)
						.join(', ')}`}</Bold>
					{o.supplerendebynavn && (
						<Description>{o.supplerendebynavn}</Description>
					)}
					<Description>{`${o.postnr} ${o.postnrnavn}`}</Description>
				</>
			)}
		/>
	)
}

function useSearch<T>(
	onSearch: (signal: AbortSignal, searchTerm?: string) => Promise<T[]>,
): (searchTerm?: string) => Promise<T[] | null> {
	const abortController = useRef<AbortController | null>()
	const abortAll = useRef(false)

	useEffect(
		() => () => {
			abortController.current?.abort()
			abortAll.current = true
		},
		[],
	)
	return async (searchTerm?: string) => {
		abortController.current?.abort()
		abortController.current = new AbortController()
		const result = await onSearch(abortController.current.signal, searchTerm)
		abortController.current = null
		return abortAll.current ? null : result
	}
}

async function vehicleSearch(
	_: AbortSignal,
	licensePrefix?: string,
): Promise<(Option & AutocompletedVehicle)[]> {
	if (!licensePrefix || licensePrefix.length < 4) return []
	try {
		const vehicles = await tinglysningApiClient.lookupVehicle(licensePrefix)
		return vehicles.map(({ id, registration, ...vehicle }) => ({
			key: id.toFixed(),
			defaultDisplayValue: registration,
			registration,
			id,
			...Object.entries(vehicle).reduce(
				(acc, [key, value]) => {
					if (typeof value !== 'string' || isNonEmpty(value)) {
						;(acc as any)[key] = value
					}
					return acc
				},
				{} as Omit<AutocompletedVehicle, 'id' | 'registration'>,
			),
		}))
	} catch (_) {
		return []
	}
}
